import StateObserver, { user } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import {
  canPlayLeague,
  canSeeLeague,
  getCurrentLeagueId,
  getEndedLeagues,
  getUnclaimedLeagues,
  hasJoinedLeague,
} from 'src/replicant/match2/leagues/leagues.getters';
import { useCache, withLoading } from 'src/lib/utils';
import { getMatch2MaxLevel } from 'src/replicant/match2/match2.getters';
import { duration } from 'src/replicant/utils/duration';
import { Feature } from 'src/replicant/analytics';
import { track } from 'src/lib/analytics/utils';
import { goToArenaFromHome } from 'src/game/championsLeague/championsLeague.sequence';
import { ChampionsLeagueOriginFeature } from 'src/replicant/championsLeague/championsLeague.analytics.schema';
import { resolvePromoGroupFeature } from 'src/game/promo/promo.launch';

/**
 * Post game sequence.
 */
export async function leaguePostGameSequence() {
  await joinLeague('postLevel');
}

/**
 * Open league leaderboard.
 */
export async function openLeagueLeaderboard(
  originFeature: ChampionsLeagueOriginFeature,
) {
  await claimLeagueRewards(originFeature);

  if (!canSeeLeague(user())) return;

  let shouldUpdateCache = true;
  const origin = mapFeatureToDialogOrigin(originFeature);

  if (!hasJoinedLeague(user())) {
    await joinLeague(origin);
    shouldUpdateCache = false;
  }

  await openPopupPromise('popupChampionsLeagueLeaderboard', {
    origin,
    originFeature,
  });
}

/**
 * Join league.
 */
export async function joinLeague(origin: PopupDialogOrigin | undefined) {
  if (!canPlayLeague(user())) return;
  if (hasJoinedLeague(user())) return;

  await openPopupPromise('popupChampionsLeagueInfo', { origin });

  // Any league intro satisfies the recurring promo for the current cycle.
  await resolvePromoGroupFeature({
    groupId: 'recurringFeatures',
    itemId: 'championsLeague',
  });

  const { leagueId } = await StateObserver.invoke.joinLeague();

  track('MasterLeagueJoin', {
    leagueId,
    currentMaxLevel: getMatch2MaxLevel(user()),
  });

  void getLeagueLeaderboard()
    .invalidate()
    .then(() => getLeagueLeaderboard().get());

  // Ensure pet goes to arena after joining league
  await goToArenaFromHome();
}

/**
 * Claim league rewards.
 */
async function claimLeagueRewards(originFeature: ChampionsLeagueOriginFeature) {
  if (getEndedLeagues(user()).length > 0) {
    await withLoading(() => StateObserver.invoke.endLeagueEvents(), {
      feature: Feature.LEAGUES._,
      subFeature: Feature.LEAGUES.END_EVENTS,
    });
  }

  const unclaimedLeagues = getUnclaimedLeagues(user());

  for (const leagueId of unclaimedLeagues) {
    await handleUnclaimedLeague(leagueId, originFeature);
  }

  if (unclaimedLeagues.length > 0) {
    await StateObserver.invoke.claimLeagueRewards();
  }
}

export async function handleUnclaimedLeague(
  leagueId: string,
  originFeature: ChampionsLeagueOriginFeature,
) {
  const leaderboard = await withLoading(
    () =>
      StateObserver.replicant.asyncGetters.getLeagueScoreboard({
        leagueId,
        friendsOnly: false,
      }),
    {
      feature: Feature.LEAGUES._,
      subFeature: Feature.LEAGUES.GET_SCOREBOARD,
    },
  );

  const origin = mapFeatureToDialogOrigin(originFeature);

  await openPopupPromise('popupChampionsLeagueClaim', {
    data: leaderboard,
    origin,
  });

  track('MasterLeagueEnd', {
    leagueId,
    currentMaxLevel: getMatch2MaxLevel(user()),
    previousMaxLevel: Number(leagueId),
    finalPosition: leaderboard.playerRank,
    phase: (user().match2.leagues[leagueId]?.score ?? 0) + 1,
  });
}

/**
 * Get league cache.
 */
export function getLeagueLeaderboard(friendsOnly = false) {
  const currentLeague = getCurrentLeagueId(user());
  return useCache(
    () =>
      StateObserver.replicant.asyncGetters.getLeagueScoreboard({
        leagueId: currentLeague,
        friendsOnly,
      }),
    `maseters_league${currentLeague}${friendsOnly ? '_friends' : ''}`,
    duration({ seconds: 10 }),
  );
}

/**
 * Get the current league's leaderboard for a player who can see the league.
 *
 * Players who cannot see the league get `undefined` without a search. A failed fetch also gives
 * `undefined`, so league UI stays hidden rather than reporting search backend errors.
 */
export async function getVisibleLeagueLeaderboard(canSee: boolean) {
  if (!canSee) return undefined;

  const cache = getLeagueLeaderboard();
  return cache.getCached() ?? cache.get().catch(() => undefined);
}
